import styled from 'styled-components';

import { Alert, Chip, Divider, Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NumberSkeletonArray, TelnyxNumberGrid, TelnyxNumberList } from '../VoiceApplication/TelnyxNumberList';
import { Label } from '@common/Label';
import { CheckCircleOutline } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@common/Button';
import { enqueueSnackbar } from 'notistack';
import { getWebhookEntity, WebhookEntityParams, WebhookEntityResponse } from '@requests/flow';
import { useTheme } from '../../../styles';
import { RequestError } from '@requests/common';

import {
  getNumberByMessagingProfile,
  MessageProfileNumber,
  TelnyxMessagingProfile,
  UpdateMessageWebhookParams,
  updateMessagingProfileWebhook,
} from '@requests/telnyx';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
  gap: 1rem;

  #info {
    font-size: 12px;
    color: ${({ theme }) => theme.text.subtext};
  }

  #error {
    font-size: 12px;
    color: ${({ theme }) => theme.telnyx.error};
  }
`;

const WebhookURL = styled.div`
  display: flex;
  align-items: center;

  font-size: 14px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.line.light};
  padding: 10px 12px;
  overflow-x: scroll;

  ::-webkit-scrollbar {
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.scrollbar.track};
    border-radius: 0 0 6px 6px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.scrollbar.thumb};
    border-radius: 0 0 6px 6px;
  }

  p {
    white-space: nowrap;
    display: inline-block;
    color: ${({ theme }) => theme.text.standard};
  }

  svg {
    color: #0cc698;
    margin-right: 12px;
    font-size: 18px;
  }
`;

export const MessagingProfileConfigure: React.FC<{ profile: TelnyxMessagingProfile }> = ({ profile }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const v1_secret = profile?.webhook_api_version === '1' ? profile.v1_secret : undefined;

  const { service_id, workflow_id } = useParams();

  const {
    data: numbers = [],
    isLoading: isNumbersLoading,
    error: numberError,
  } = useQuery<MessageProfileNumber[], RequestError, MessageProfileNumber[], [string, string | undefined]>({
    queryKey: ['messaging_number', profile.id],
    queryFn: getNumberByMessagingProfile,
    retry: false,
  });

  const {
    data: entity = null,
    isLoading: entityLoading,
    error: webhookEntityError,
  } = useQuery<WebhookEntityResponse, RequestError, WebhookEntityResponse, [string, WebhookEntityParams]>({
    queryKey: ['webhook_entity', { service_id, workflow_id, profile_id: profile.id, type: 'messaging', v1_secret }],
    queryFn: getWebhookEntity,
    enabled: Boolean(profile.id && service_id && workflow_id),
    retry: false,
  });

  const getWebhookURL = () => {
    if (!entity?.webhook_id) return undefined;
    if (LOCAL) return `https://noc-telnyx-eu.ngrok.io/event/${entity?.webhook_id}`;

    const isInternalProd = INTERNAL_CLIENT && window.location.origin.includes('prod');
    const isInternalDev = INTERNAL_CLIENT && !isInternalProd;

    if (window.location.origin.includes('dev') || isInternalDev)
      return `https://apidev.telnyx.com/v2/flow/event/${entity?.webhook_id}`;

    return `https://api.telnyx.com/v2/flow/event/${entity?.webhook_id}`;
  };

  const webhookUrl = getWebhookURL();
  const webhookConfigured = Boolean(profile.webhook_url);
  const flowWebhookConfigured = profile.webhook_url === webhookUrl && !entityLoading;

  const entityAssigned = entity?.duplicate;
  const configured = webhookConfigured && flowWebhookConfigured && !entityAssigned;
  const configuredIncorrectly = webhookConfigured && !flowWebhookConfigured && !entityLoading && !entityAssigned;
  const notConfigured = !webhookConfigured && !flowWebhookConfigured && !entityLoading && !entityAssigned;

  const { mutateAsync: update, isPending: updateWebhookLoading } = useMutation<
    TelnyxMessagingProfile,
    RequestError,
    UpdateMessageWebhookParams
  >({
    mutationFn: updateMessagingProfileWebhook,
    onSuccess: () => {
      enqueueSnackbar(
        'The webhook entity was assigned successfully. You can now send inbound messages to phone numbers attached.',
        {
          variant: 'success',
          autoHideDuration: 10000,
        }
      );

      queryClient.invalidateQueries({ queryKey: ['webhook_entity'] });
      queryClient.invalidateQueries({ queryKey: ['messaging_profiles'] });
    },
    onError: (data) => {
      const errorMessage = data?.response?.data.error || 'An unexpected error occurred.';

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar('Failed to assign webhook entity to workflow!', {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const updateEventUrl = () => {
    update({ profileId: profile.id, service_id, workflow_id, v1_secret }).catch(() => null);
  };

  return (
    <Container>
      {webhookEntityError ? (
        <Stack spacing={1} mt={1}>
          <Label
            title='Webhook URL'
            tooltip='This is the endpoint where inbound SMS will be sent. This must point to your workflow otherwise it will not work.'
          />
          <Alert severity='error'>{webhookEntityError?.response?.data?.error || webhookEntityError?.message}</Alert>
        </Stack>
      ) : (
        <Stack spacing={1} mt={1}>
          <Label
            title='Webhook URL'
            tooltip='This is the endpoint where inbound SMS will be sent. This must point to your workflow otherwise it will not work.'
          />
          {entityAssigned && <Alert severity='error'>{entityAssigned}</Alert>}
          {configuredIncorrectly && !updateWebhookLoading && (
            <Alert severity='warning'>
              This messaging profiles webhook needs to be updated to point to this workflow. This will{' '}
              <strong>OVERWRITE</strong> the existing webhook.
            </Alert>
          )}
          {notConfigured && !updateWebhookLoading && (
            <Alert severity='error'>
              This messaging profile does not have a webhook assigned. This application needs to be updated to point to
              this workflow.
            </Alert>
          )}
          {profile.webhook_url && (
            <WebhookURL>
              {configured && <CheckCircleOutline />}
              <p>{profile.webhook_url}</p>
            </WebhookURL>
          )}
          {configured && <p id='info'>A unique webhook address has been assigned to this workflow.</p>}
          {(configuredIncorrectly || notConfigured) && (
            <LoadingButton
              minHeight='32px'
              loading={updateWebhookLoading}
              customColor='primary'
              size='small'
              onClick={updateEventUrl}
            >
              Update Webhook
            </LoadingButton>
          )}
          {entityAssigned && (
            <Stack spacing={1}>
              <LoadingButton
                minHeight='32px'
                loading={updateWebhookLoading}
                customColor='error'
                size='small'
                onClick={updateEventUrl}
              >
                Update & Remove
              </LoadingButton>
              <p id='error'>
                Assigning a webhook entity to this workflow will remove the webhook entity from your other workflow.
                This will cause the other workflow to stop working.
              </p>
            </Stack>
          )}
        </Stack>
      )}

      <Divider flexItem orientation='horizontal' />
      {numberError ? (
        <Stack spacing={1}>
          <Label
            title='Phone Numbers'
            tooltip='These are the phone numbers associated with this messaging profile. You can assign many phone numbers to a single profile.'
          />
          <Alert severity='error'>
            {numberError?.response?.data?.error || 'An unexpected error occurred loading the numbers.'}
          </Alert>
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Label
            title='Phone Numbers'
            tooltip='These are the phone numbers associated with this messaging profile. You can assign many phone numbers to a single profile.'
          />
          {isNumbersLoading && <NumberSkeletonArray />}
          {!isNumbersLoading && numbers.length === 0 && (
            <Stack spacing={2}>
              <Alert severity='error'>
                You cannot trigger this workflow until you assign a phone number to your messaging profile. This is done
                from the mission control portal{' '}
                <a
                  style={{ color: theme.primary }}
                  href={
                    window.location.origin.includes('dev')
                      ? 'https://portaldev.telnyx.com/#/numbers/my-numbers'
                      : 'https://portal.telnyx.com/#/numbers/my-numbers'
                  }
                  target='_blank'
                  rel='noreferrer'
                >
                  here
                </a>
                .
              </Alert>
              <Chip color='error' variant='outlined' size='small' label='No Phone Numbers Assigned' />
            </Stack>
          )}
          <TelnyxNumberGrid>
            {numbers.map((number) => (
              <TelnyxNumberList key={number.id} number={number} />
            ))}
          </TelnyxNumberGrid>
        </Stack>
      )}
    </Container>
  );
};
